import { useState, useCallback, useRef, useEffect } from 'react';
import {
  roaryApi,
  type RoaryEvent,
  type RoarySession,
  type MarshmallowUpdate,
} from '../api/modules';
import type { BuildPageStatus, RoarySessionMode } from '../api/modules/roary';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface QuickReply {
  id: string;
  label: string;
  value: string;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
}

export type ConversationPhase =
  | 'greeting' // Welcome message, waiting for user choice
  | 'chatting' // Conversational mode (discovery, questions, etc.) — backend-driven
  | 'building' // Active building via backend API (tools running)
  | 'complete'; // Build complete — show celebration

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  quickReplies?: QuickReply[];
  toolCalls?: {
    name: string;
    status: 'running' | 'completed' | 'failed';
    result?: unknown;
  }[];
}

export interface RoaryOperationDisplay {
  id: string;
  type: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
}

export interface RoaryCompletionData {
  siteId: string;
  siteName: string;
  pagesCreated: number;
  componentsCreated: number;
  previewUrl?: string;
  homePageId?: string;
  // Populated when a build_site run ends. Undefined for non-build completions
  // (e.g. conversational turns) so existing behavior is preserved.
  pagesVerified?: number;
  pagesFailed?: number;
  failedPages?: Array<{ slug: string; title: string; issues: string[] }>;
  layoutsVerified?: number;
  layoutsFailed?: number;
  failedLayouts?: Array<{ name: string; issues: string[] }>;
  stopReason?: string;
  // Page-mode completions surface the specific page that was built so the UI
  // can deep-link the user into its editor.
  mode?: RoarySessionMode;
  pageId?: string;
  pageTitle?: string;
  pageSlug?: string;
}

export interface RoaryBuildPageProgress {
  slug: string;
  title: string;
  status: BuildPageStatus;
  componentsUpdated: number;
  attempts: number;
  issues: string[];
}

export interface RoaryBuildLayoutProgress {
  layoutId: string;
  name: string;
  status: BuildPageStatus;
  componentsUpdated: number;
  attempts: number;
  issues: string[];
}

export interface RoaryBuildProgress {
  plan: {
    businessName: string;
    businessType: string;
    templateName: string;
  } | null;
  phase: 'idle' | 'planning' | 'scaffolding' | 'building_layouts' | 'building_pages' | 'completed';
  pages: RoaryBuildPageProgress[];
  layouts: RoaryBuildLayoutProgress[];
  currentPageIndex: number | null;
  currentLayoutIndex: number | null;
}

export interface UseRoaryState {
  sessionId: string | null;
  session: RoarySession | null;
  messages: ChatMessage[];
  operations: RoaryOperationDisplay[];
  completionData: RoaryCompletionData | null;
  buildProgress: RoaryBuildProgress | null;
  isLoading: boolean;
  isStreaming: boolean;
  isThinking: boolean;
  error: string | null;
  currentToolName: string | null;
  phase: ConversationPhase;
  /** Increments when Roary asks the user to pick a layout. */
  chooseTemplateSignal: number;
}

export interface UseRoaryReturn extends UseRoaryState {
  startSession: (siteId?: string, mode?: RoarySessionMode) => Promise<string>;
  sendMessage: (content: string) => void;
  handleQuickReply: (reply: QuickReply) => void;
  cancelSession: () => Promise<void>;
  clearError: () => void;
  loadSession: (sessionId: string) => Promise<void>;
  restoreSession: (mode?: RoarySessionMode) => boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

// Import creates a new site from a source URL. The dashboard greeting
// includes it for every signed-in user; other callers can leave it off.
const BUILD_QUICK_REPLY: QuickReply = {
  id: 'build',
  label: 'Build a new website',
  value: 'build',
  icon: '🚀',
};
const IMPROVE_QUICK_REPLY: QuickReply = {
  id: 'improve',
  label: 'Improve my website',
  value: 'improve',
  icon: '✨',
};
const IMPORT_QUICK_REPLY: QuickReply = {
  id: 'import',
  label: 'Import an existing website',
  value: 'import',
  icon: '🌐',
};
const QUESTION_QUICK_REPLY: QuickReply = {
  id: 'question',
  label: 'I have a question',
  value: 'question',
  icon: '❓',
};

function buildGreetingQuickReplies(allowImport: boolean, importOnly = false): QuickReply[] {
  const replies = allowImport
    ? [BUILD_QUICK_REPLY, IMPROVE_QUICK_REPLY, IMPORT_QUICK_REPLY, QUESTION_QUICK_REPLY]
    : [BUILD_QUICK_REPLY, IMPROVE_QUICK_REPLY, QUESTION_QUICK_REPLY];
  if (!importOnly) return replies;
  // Regular users can build a new site or import one. Improve and questions stay disabled.
  return replies.map(reply =>
    reply.value === 'build' || reply.value === 'import' ? reply : { ...reply, disabled: true }
  );
}

// Page-mode greeting offers common page types as one-tap suggestions.
const PAGE_MODE_QUICK_REPLIES: QuickReply[] = [
  { id: 'about', label: 'About page', value: 'about', icon: '👋', variant: 'primary' },
  { id: 'pricing', label: 'Pricing page', value: 'pricing', icon: '💰' },
  { id: 'contact', label: 'Contact page', value: 'contact', icon: '📬' },
  { id: 'services', label: 'Services page', value: 'services', icon: '🛠️' },
];

// ─── Pure Helper Functions ──────────────────────────────────────────────────────

function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ─── Session Memory (localStorage) ──────────────────────────────────────────────
// Roary conversations are scoped per-user. Both the storage *key* is suffixed
// with the current user's id and the saved blob carries the userId for a
// belt-and-suspenders check on load. This prevents leaking one user's chat
// to another after a sign-out / sign-in on the same browser (which is what
// happened when this used a single global key).

const ROARY_STORAGE_KEY_PREFIX = 'roary_session_memory';
const MEMORY_EXPIRY_HOURS = 24;

/** Decode the current user's id from the JWT in localStorage. Returns null
 *  if there is no token or it can't be parsed — caller treats that as
 *  "don't touch the cache." */
function getCurrentUserIdFromToken(): string | null {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const userId = (payload as { userId?: unknown }).userId;
    return typeof userId === 'string' ? userId : null;
  } catch {
    return null;
  }
}

function storageKeyFor(userId: string): string {
  return `${ROARY_STORAGE_KEY_PREFIX}:${userId}`;
}

interface RoarySessionMemory {
  sessionId: string;
  /** Owner of this memory. Cross-checked on load — mismatch means the cache
   *  is stale (different user signed in) and we discard it. */
  userId: string;
  phase: ConversationPhase;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
    quickReplies?: QuickReply[];
  }>;
  completionData: RoaryCompletionData | null;
  savedAt: string;
}

function saveSessionToStorage(state: UseRoaryState): void {
  if (!state.sessionId) return;
  const userId = getCurrentUserIdFromToken();
  if (!userId) return; // anonymous / token gone — don't persist
  const memory: RoarySessionMemory = {
    sessionId: state.sessionId,
    userId,
    phase: state.phase,
    messages: state.messages
      .filter(m => !m.id.startsWith('welcome-back-') && (!m.isStreaming || m.content))
      .map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
        quickReplies: m.quickReplies,
      })),
    completionData: state.completionData,
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(storageKeyFor(userId), JSON.stringify(memory));
  } catch {
    // localStorage full or unavailable
  }
}

function loadSessionFromStorage(): RoarySessionMemory | null {
  const userId = getCurrentUserIdFromToken();
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return null;
    const memory: RoarySessionMemory = JSON.parse(raw);

    // Defense in depth: even though we keyed by user, verify the embedded
    // userId matches the current one. If somehow the key was reused for a
    // different user (shared browser, manual edit, future code mistake),
    // refuse to restore and wipe it.
    if (memory.userId !== userId) {
      localStorage.removeItem(storageKeyFor(userId));
      return null;
    }

    const savedAt = new Date(memory.savedAt);
    const hoursElapsed = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
    if (hoursElapsed > MEMORY_EXPIRY_HOURS) {
      localStorage.removeItem(storageKeyFor(userId));
      return null;
    }

    return memory;
  } catch {
    return null;
  }
}

function clearSessionStorage(): void {
  // Clear the current user's memory. Other users' caches (if any) stay
  // untouched — they're keyed under their own userId.
  const userId = getCurrentUserIdFromToken();
  if (userId) {
    localStorage.removeItem(storageKeyFor(userId));
  }
  // Also drop the legacy unscoped key from before this fix, if present.
  localStorage.removeItem(ROARY_STORAGE_KEY_PREFIX);
}

/** Wipes ALL Roary session caches in this browser. Call on logout so the
 *  next signed-in user can never hydrate a previous user's chat. */
export function clearAllRoarySessionStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key === ROARY_STORAGE_KEY_PREFIX || key.startsWith(`${ROARY_STORAGE_KEY_PREFIX}:`))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    // localStorage unavailable — nothing to do
  }
}

export interface UseRoaryOptions {
  onMarshmallowUpdate?: (data: MarshmallowUpdate) => void;
  onMarshmallowDepleted?: (data: { remaining: number; total: number; resetDate: string }) => void;
  // Gates the "Import existing website" quick reply. Defaults to false.
  allowImport?: boolean;
  // Regular users can build a new site or import one. Improve and questions stay disabled.
  importOnly?: boolean;
}

// ─── Main Hook ──────────────────────────────────────────────────────────────────

export const useRoary = (options?: UseRoaryOptions): UseRoaryReturn => {
  const [state, setState] = useState<UseRoaryState>({
    sessionId: null,
    session: null,
    messages: [],
    operations: [],
    completionData: null,
    buildProgress: null,
    isLoading: false,
    isStreaming: false,
    isThinking: false,
    error: null,
    currentToolName: null,
    phase: 'greeting',
    chooseTemplateSignal: 0,
  });

  const abortRef = useRef<(() => void) | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // A site or page build finished in this turn, even if some sections failed.
  const siteBuildFinishedRef = useRef(false);

  // Keep sessionId ref in sync for use in callbacks
  useEffect(() => {
    sessionIdRef.current = state.sessionId;
  }, [state.sessionId]);

  // ─── State Helpers ──────────────────────────────────────────────────────────

  const updateLastAssistantMessage = useCallback((update: Partial<ChatMessage>) => {
    setState(prev => {
      const messages = [...prev.messages];
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          messages[i] = { ...messages[i], ...update };
          break;
        }
      }
      return { ...prev, messages };
    });
  }, []);

  // ─── Session Management ─────────────────────────────────────────────────────

  const startSession = useCallback(
    async (siteId?: string, mode: RoarySessionMode = 'site'): Promise<string> => {
      clearSessionStorage();
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await roaryApi.createSession(siteId, mode);

        // Page-mode opens with a focused single-page prompt; site-mode keeps
        // the original "what brings you here" greeting with quick replies.
        const welcomeMessage: ChatMessage =
          mode === 'page'
            ? {
                id: makeId('welcome'),
                role: 'assistant',
                content:
                  "Hey! I'll add one page to your site this round. Which page should we build? Pick one below or just tell me what you have in mind.",
                timestamp: new Date(),
                quickReplies: PAGE_MODE_QUICK_REPLIES,
              }
            : {
                id: makeId('welcome'),
                role: 'assistant',
                content:
                  "Hey there! I'm Roary - the world's first dragon who builds websites instead of burning them down. Much more productive, if you ask me.\n\nI work here at Compete Pages, and I'm pretty good at what I do. What brings you here today?",
                timestamp: new Date(),
                quickReplies: buildGreetingQuickReplies(
                  options?.allowImport ?? false,
                  options?.importOnly ?? false
                ),
              };

        setState(prev => ({
          ...prev,
          sessionId: response.sessionId,
          session: null,
          messages: [welcomeMessage],
          operations: [],
          completionData: null,
          buildProgress: null,
          isLoading: false,
          phase: 'greeting',
          chooseTemplateSignal: 0,
        }));

        return response.sessionId;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to start session';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        throw error;
      }
    },
    []
  );

  const loadSession = useCallback(async (sessionId: string): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await roaryApi.getSession(sessionId);

      const chatMessages: ChatMessage[] = data.messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date(m.createdAt),
        }));

      setState(prev => ({
        ...prev,
        sessionId,
        session: data.session,
        messages: chatMessages,
        isLoading: false,
        phase: 'chatting',
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load session';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const cancelSession = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }

    if (!state.sessionId) return;

    try {
      await roaryApi.cancelSession(state.sessionId);
      setState(prev => ({
        ...prev,
        isStreaming: false,
        isThinking: false,
        currentToolName: null,
      }));
    } catch (error) {
      console.error('Failed to cancel session:', error);
    }
  }, [state.sessionId]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // ─── Session Restore ──────────────────────────────────────────────────────

  const restoreSession = useCallback((mode: RoarySessionMode = 'site'): boolean => {
    const memory = loadSessionFromStorage();
    if (!memory) return false;

    // If was building when closed, fall back to chatting
    let restoredPhase = memory.phase;
    if (restoredPhase === 'building') {
      restoredPhase = 'chatting';
    }

    // Drop prior welcome-backs so reopen never stacks them.
    const restoredMessages: ChatMessage[] = memory.messages
      .filter(m => !m.id.startsWith('welcome-back-'))
      .map(m => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));

    // If they never left the first greeting, keep that bubble only.
    // A second "welcome back" with the same buttons is just a duplicate.
    const stillOnGreeting =
      restoredPhase === 'greeting' && !restoredMessages.some(m => m.role === 'user');

    // Stale greeting replies live in localStorage — restamp them so copy
    // changes (e.g. new menu labels) show up without starting a new session.
    if (stillOnGreeting) {
      const currentReplies = buildGreetingQuickReplies(
        options?.allowImport ?? false,
        options?.importOnly ?? false
      );
      for (const message of restoredMessages) {
        if (message.quickReplies?.length) {
          message.quickReplies = currentReplies;
        }
      }
    }

    if (!stillOnGreeting) {
      restoredMessages.push({
        id: makeId('welcome-back'),
        role: 'assistant',
        content:
          'Welcome back! Still here, still breathing fire (only metaphorically). What would you like to do?',
        timestamp: new Date(),
        quickReplies:
          mode === 'page'
            ? PAGE_MODE_QUICK_REPLIES
            : buildGreetingQuickReplies(
                options?.allowImport ?? false,
                options?.importOnly ?? false
              ),
      });
    }

    setState(prev => ({
      ...prev,
      sessionId: memory.sessionId,
      phase: restoredPhase,
      messages: restoredMessages,
      completionData: memory.completionData,
      // Build progress is ephemeral — if a build was in-flight when the tab
      // closed it's no longer running on the server, so start clean.
      buildProgress: null,
      isLoading: false,
      chooseTemplateSignal: 0,
    }));

    return true;
  }, []);

  // ─── Backend Streaming ──────────────────────────────────────────────────────

  const streamToBackend = useCallback(
    (content: string) => {
      const currentSessionId = sessionIdRef.current;
      if (!currentSessionId || state.isStreaming) return;

      siteBuildFinishedRef.current = false;

      // User message
      const userMessage: ChatMessage = {
        id: makeId('user'),
        role: 'user',
        content,
        timestamp: new Date(),
      };

      // Placeholder assistant message for streaming
      const assistantPlaceholder: ChatMessage = {
        id: makeId('assistant'),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
        toolCalls: [],
      };

      setState(prev => ({
        ...prev,
        // Clear quick replies from previous messages, add new messages
        messages: [
          ...prev.messages.map(m => ({ ...m, quickReplies: undefined })),
          userMessage,
          assistantPlaceholder,
        ],
        // Clear any stale build progress from a previous turn.
        buildProgress: null,
        isStreaming: true,
        isThinking: true,
        error: null,
        // Transition from greeting to chatting on first message
        phase: prev.phase === 'greeting' ? 'chatting' : prev.phase,
      }));

      // Handle SSE events
      const handleEvent = (event: RoaryEvent) => {
        const data = event.data as Record<string, unknown>;

        switch (event.type) {
          case 'connected':
            break;

          case 'thinking':
            setState(prev => ({ ...prev, isThinking: true }));
            break;

          case 'text_delta': {
            // Real-time streaming: append delta text to the last assistant message
            const deltaText = data.content as string;
            if (deltaText) {
              setState(prev => {
                const messages = [...prev.messages];
                for (let i = messages.length - 1; i >= 0; i--) {
                  if (messages[i].role === 'assistant' && messages[i].isStreaming) {
                    messages[i] = {
                      ...messages[i],
                      content: messages[i].content + deltaText,
                    };
                    break;
                  }
                }
                return { ...prev, messages, isThinking: false };
              });
            }
            break;
          }

          case 'message':
            // Authoritative final content — replaces accumulated deltas with clean text
            if (data.role === 'assistant' && typeof data.content === 'string') {
              updateLastAssistantMessage({
                content: data.content,
                isStreaming: true,
              });
              setState(prev => ({ ...prev, isThinking: false }));
            }
            break;

          case 'choose_template':
            setState(prev => ({
              ...prev,
              chooseTemplateSignal: prev.chooseTemplateSignal + 1,
            }));
            break;

          case 'suggestions': {
            // Backend-driven quick replies — attach to last assistant message
            const suggestions = data.suggestions as
              | Array<{
                  id: string;
                  label: string;
                  value: string;
                  icon?: string;
                  variant?: string;
                }>
              | undefined;
            if (suggestions) {
              updateLastAssistantMessage({
                quickReplies: suggestions.map(s => ({
                  id: s.id,
                  label: s.label,
                  value: s.value,
                  icon: s.icon,
                  variant: (s.variant as 'primary' | 'secondary') || undefined,
                })),
              });
            }
            break;
          }

          case 'tool_start': {
            const toolId = (data.toolId as string) || `op-${Date.now()}`;
            setState(prev => {
              const messages = [...prev.messages];
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'assistant' && messages[i].isStreaming) {
                  messages[i] = {
                    ...messages[i],
                    toolCalls: [
                      ...(messages[i].toolCalls || []),
                      { name: data.toolName as string, status: 'running' },
                    ],
                  };
                  break;
                }
              }
              const newOperation: RoaryOperationDisplay = {
                id: toolId,
                type: data.toolName as string,
                description: (data.description as string) || (data.toolName as string),
                status: 'running',
                startedAt: new Date().toISOString(),
              };
              return {
                ...prev,
                isThinking: false,
                currentToolName: data.toolName as string,
                messages,
                operations: [...prev.operations, newOperation],
                // Tools running means we're building
                phase: 'building',
              };
            });
            break;
          }

          case 'tool_end': {
            const toolId = data.toolId as string;
            setState(prev => {
              const messages = [...prev.messages];
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'assistant' && messages[i].toolCalls) {
                  let updated = false;
                  const toolCalls = messages[i].toolCalls!.map(tc => {
                    if (
                      !updated &&
                      tc.name === (data.toolName as string) &&
                      tc.status === 'running'
                    ) {
                      updated = true;
                      return {
                        ...tc,
                        status: (data.success ? 'completed' : 'failed') as 'completed' | 'failed',
                        result: data.result,
                      };
                    }
                    return tc;
                  });
                  messages[i] = { ...messages[i], toolCalls };
                  break;
                }
              }

              let foundById = false;
              const operations = prev.operations.map(op => {
                if (toolId && op.id === toolId) {
                  foundById = true;
                  return {
                    ...op,
                    status: (data.success ? 'completed' : 'failed') as 'completed' | 'failed',
                    completedAt: new Date().toISOString(),
                  };
                }
                return op;
              });

              if (!foundById) {
                let updated = false;
                return {
                  ...prev,
                  currentToolName: null,
                  messages,
                  operations: prev.operations.map(op => {
                    if (
                      !updated &&
                      op.type === (data.toolName as string) &&
                      op.status === 'running'
                    ) {
                      updated = true;
                      return {
                        ...op,
                        status: (data.success ? 'completed' : 'failed') as 'completed' | 'failed',
                        completedAt: new Date().toISOString(),
                      };
                    }
                    return op;
                  }),
                };
              }
              return { ...prev, currentToolName: null, messages, operations };
            });
            break;
          }

          case 'complete': {
            const isFinal = (data.isFinalBuild as boolean) || siteBuildFinishedRef.current;
            setState(prev => {
              const messages = [...prev.messages];
              // Mark last assistant message as not streaming
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'assistant') {
                  messages[i] = { ...messages[i], isStreaming: false };
                  break;
                }
              }

              // Merge: the `complete` event carries DB-derived page and
              // component counts; prior `build_completed` (if any) carried
              // verification stats. Keep both.
              let nextCompletion: RoaryCompletionData | null = prev.completionData;
              if (data.siteId && data.siteName) {
                nextCompletion = {
                  ...(prev.completionData || ({} as Partial<RoaryCompletionData>)),
                  siteId: data.siteId as string,
                  siteName: data.siteName as string,
                  pagesCreated:
                    (data.pagesCreated as number) || prev.completionData?.pagesCreated || 0,
                  componentsCreated:
                    (data.componentsCreated as number) ||
                    prev.completionData?.componentsCreated ||
                    0,
                  previewUrl: data.previewUrl as string | undefined,
                  homePageId:
                    (data.homePageId as string | undefined) || prev.completionData?.homePageId,
                  // Page-mode fields
                  mode: (data.mode as RoarySessionMode | undefined) || prev.completionData?.mode,
                  pageId: (data.pageId as string | undefined) || prev.completionData?.pageId,
                  pageTitle:
                    (data.pageTitle as string | undefined) || prev.completionData?.pageTitle,
                  pageSlug: (data.pageSlug as string | undefined) || prev.completionData?.pageSlug,
                } as RoaryCompletionData;
              }

              const hasSite = Boolean((data.siteId as string) || prev.completionData?.siteId);

              return {
                ...prev,
                messages,
                isStreaming: false,
                isThinking: false,
                currentToolName: null,
                completionData: nextCompletion,
                // A finished site build always opens the edit / view screen,
                // including when some pages failed verification.
                phase: isFinal && hasSite ? 'complete' : 'chatting',
              };
            });
            break;
          }

          case 'marshmallow_update':
            options?.onMarshmallowUpdate?.(data as unknown as MarshmallowUpdate);
            break;

          case 'marshmallow_depleted':
            options?.onMarshmallowDepleted?.(
              data as unknown as { remaining: number; total: number; resetDate: string }
            );
            break;

          case 'error':
            setState(prev => ({
              ...prev,
              error: (data.message as string) || 'An error occurred',
              isStreaming: false,
              isThinking: false,
              currentToolName: null,
              phase:
                siteBuildFinishedRef.current && prev.completionData?.siteId
                  ? 'complete'
                  : prev.phase,
            }));
            updateLastAssistantMessage({ isStreaming: false });
            break;

          case 'done':
            setState(prev => {
              const messages = [...prev.messages];
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'assistant') {
                  messages[i] = { ...messages[i], isStreaming: false };
                  break;
                }
              }
              return {
                ...prev,
                messages,
                isStreaming: false,
                isThinking: false,
                currentToolName: null,
              };
            });
            break;

          case 'cancelled':
            updateLastAssistantMessage({
              isStreaming: false,
              content: 'Session was cancelled.',
            });
            setState(prev => ({
              ...prev,
              isStreaming: false,
              isThinking: false,
              currentToolName: null,
            }));
            break;

          // ─── Agentic build pipeline events ─────────────────────────────
          case 'build_plan_ready': {
            const plan =
              (data.plan as {
                businessName: string;
                businessType: string;
                templateName: string;
                pages: Array<{ slug: string; title: string; intent: string }>;
              }) || null;
            if (!plan) break;
            setState(prev => ({
              ...prev,
              phase: 'building',
              buildProgress: {
                plan: {
                  businessName: plan.businessName,
                  businessType: plan.businessType,
                  templateName: plan.templateName,
                },
                phase: 'scaffolding',
                currentPageIndex: null,
                currentLayoutIndex: null,
                pages: plan.pages.map(p => ({
                  slug: p.slug,
                  title: p.title,
                  status: 'pending',
                  componentsUpdated: 0,
                  attempts: 0,
                  issues: [],
                })),
                // Layouts are discovered by the orchestrator after scaffold;
                // the per-layout event stream will populate this list.
                layouts: [],
              },
            }));
            break;
          }

          case 'build_scaffold_started':
            setState(prev => ({
              ...prev,
              buildProgress: prev.buildProgress
                ? { ...prev.buildProgress, phase: 'scaffolding' }
                : prev.buildProgress,
            }));
            break;

          case 'build_scaffold_completed':
            setState(prev => ({
              ...prev,
              buildProgress: prev.buildProgress
                ? { ...prev.buildProgress, phase: 'building_layouts' }
                : prev.buildProgress,
            }));
            break;

          case 'build_layout_started': {
            const layoutIndex = data.layoutIndex as number;
            const layoutId = data.layoutId as string;
            const name = (data.name as string) || 'Layout';
            setState(prev => {
              if (!prev.buildProgress) return prev;
              // The orchestrator doesn't pre-announce layouts, so we grow the
              // list as events arrive. Upsert by index.
              const layouts = [...prev.buildProgress.layouts];
              while (layouts.length <= layoutIndex) {
                layouts.push({
                  layoutId: '',
                  name: 'Layout',
                  status: 'pending',
                  componentsUpdated: 0,
                  attempts: 0,
                  issues: [],
                });
              }
              layouts[layoutIndex] = {
                ...layouts[layoutIndex],
                layoutId,
                name,
                status: 'customizing',
                attempts: Math.max(layouts[layoutIndex].attempts, 1),
              };
              return {
                ...prev,
                buildProgress: {
                  ...prev.buildProgress,
                  phase: 'building_layouts',
                  currentLayoutIndex: layoutIndex,
                  layouts,
                },
              };
            });
            break;
          }

          case 'build_layout_customized': {
            const layoutIndex = data.layoutIndex as number;
            const componentsUpdated = (data.componentsUpdated as number) || 0;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const layouts = prev.buildProgress.layouts.map((l, i) =>
                i === layoutIndex
                  ? { ...l, componentsUpdated: l.componentsUpdated + componentsUpdated }
                  : l
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, layouts },
              };
            });
            break;
          }

          case 'build_layout_verifying': {
            const layoutIndex = data.layoutIndex as number;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const layouts = prev.buildProgress.layouts.map((l, i) =>
                i === layoutIndex ? { ...l, status: 'verifying' as BuildPageStatus } : l
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, layouts },
              };
            });
            break;
          }

          case 'build_layout_verified': {
            const layoutIndex = data.layoutIndex as number;
            const verified = data.verified as boolean;
            const issues = (data.issues as string[]) || [];
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const layouts = prev.buildProgress.layouts.map((l, i) =>
                i === layoutIndex
                  ? {
                      ...l,
                      // The orchestrator emits build_layout_failed separately
                      // once it exhausts the retry budget; until then treat
                      // any unverified layout as "retrying".
                      status: verified
                        ? ('verified' as BuildPageStatus)
                        : ('retrying' as BuildPageStatus),
                      issues: verified ? [] : issues,
                    }
                  : l
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, layouts },
              };
            });
            break;
          }

          case 'build_layout_retrying': {
            const layoutIndex = data.layoutIndex as number;
            const attempt = (data.attempt as number) || 2;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const layouts = prev.buildProgress.layouts.map((l, i) =>
                i === layoutIndex
                  ? { ...l, status: 'retrying' as BuildPageStatus, attempts: attempt }
                  : l
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, layouts },
              };
            });
            break;
          }

          case 'build_layout_failed': {
            const layoutIndex = data.layoutIndex as number;
            const issues = (data.issues as string[]) || [];
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const layouts = prev.buildProgress.layouts.map((l, i) =>
                i === layoutIndex ? { ...l, status: 'failed' as BuildPageStatus, issues } : l
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, layouts },
              };
            });
            break;
          }

          case 'build_page_started': {
            const pageIndex = data.pageIndex as number;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const pages = prev.buildProgress.pages.map((p, i) =>
                i === pageIndex
                  ? {
                      ...p,
                      status: 'customizing' as BuildPageStatus,
                      attempts: Math.max(p.attempts, 1),
                    }
                  : p
              );
              return {
                ...prev,
                buildProgress: {
                  ...prev.buildProgress,
                  phase: 'building_pages',
                  // Pages start after layouts — clear the layout pointer so
                  // the UI can move focus to the pages list.
                  currentLayoutIndex: null,
                  currentPageIndex: pageIndex,
                  pages,
                },
              };
            });
            break;
          }

          case 'build_page_customized': {
            const pageIndex = data.pageIndex as number;
            const componentsUpdated = (data.componentsUpdated as number) || 0;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const pages = prev.buildProgress.pages.map((p, i) =>
                i === pageIndex
                  ? { ...p, componentsUpdated: p.componentsUpdated + componentsUpdated }
                  : p
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, pages },
              };
            });
            break;
          }

          case 'build_page_verifying': {
            const pageIndex = data.pageIndex as number;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const pages = prev.buildProgress.pages.map((p, i) =>
                i === pageIndex ? { ...p, status: 'verifying' as BuildPageStatus } : p
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, pages },
              };
            });
            break;
          }

          case 'build_page_verified': {
            const pageIndex = data.pageIndex as number;
            const verified = data.verified as boolean;
            const issues = (data.issues as string[]) || [];
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const pages = prev.buildProgress.pages.map((p, i) =>
                i === pageIndex
                  ? {
                      ...p,
                      // If verified, mark verified. If not verified, mark as
                      // retrying — the orchestrator will emit
                      // build_page_failed separately when it genuinely gives
                      // up (after the full retry budget).
                      status: verified
                        ? ('verified' as BuildPageStatus)
                        : ('retrying' as BuildPageStatus),
                      issues: verified ? [] : issues,
                    }
                  : p
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, pages },
              };
            });
            break;
          }

          case 'build_page_retrying': {
            const pageIndex = data.pageIndex as number;
            const attempt = (data.attempt as number) || 2;
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const pages = prev.buildProgress.pages.map((p, i) =>
                i === pageIndex
                  ? {
                      ...p,
                      status: 'retrying' as BuildPageStatus,
                      attempts: attempt,
                    }
                  : p
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, pages },
              };
            });
            break;
          }

          case 'build_page_failed': {
            const pageIndex = data.pageIndex as number;
            const issues = (data.issues as string[]) || [];
            setState(prev => {
              if (!prev.buildProgress) return prev;
              const pages = prev.buildProgress.pages.map((p, i) =>
                i === pageIndex ? { ...p, status: 'failed' as BuildPageStatus, issues } : p
              );
              return {
                ...prev,
                buildProgress: { ...prev.buildProgress, pages },
              };
            });
            break;
          }

          case 'build_completed': {
            const summary = data.summary as
              | {
                  pagesPlanned: number;
                  pagesCustomized: number;
                  pagesVerified: number;
                  pagesRetried: number;
                  pagesFailed: number;
                  layoutsProcessed: number;
                  layoutsVerified: number;
                  layoutsFailed: number;
                }
              | undefined;
            const pagesInfo =
              (data.pages as Array<{
                pageId: string;
                slug: string;
                title: string;
                status: BuildPageStatus;
                issues: string[];
              }>) || [];
            const layoutsInfo =
              (data.layouts as Array<{
                layoutId: string;
                name: string;
                status: BuildPageStatus;
                issues: string[];
              }>) || [];
            const stopReason = data.stopReason as string | undefined;
            if (data.siteId) siteBuildFinishedRef.current = true;

            setState(prev => {
              const updatedBuildProgress: RoaryBuildProgress | null = prev.buildProgress
                ? {
                    ...prev.buildProgress,
                    phase: 'completed',
                    currentPageIndex: null,
                    currentLayoutIndex: null,
                    pages: prev.buildProgress.pages.map((p, i) => {
                      const incoming = pagesInfo[i];
                      if (!incoming) return p;
                      return {
                        ...p,
                        status: incoming.status,
                        issues: incoming.issues,
                      };
                    }),
                    layouts: prev.buildProgress.layouts.map((l, i) => {
                      const incoming = layoutsInfo[i];
                      if (!incoming) return l;
                      return {
                        ...l,
                        status: incoming.status,
                        issues: incoming.issues,
                      };
                    }),
                  }
                : null;

              // Build a completion object that preserves the legacy shape
              // (pagesCreated / componentsCreated) while adding the verification
              // fields. The final `complete` event from RoaryService will also
              // fire and fill in siteId / siteName.
              const failedPages = pagesInfo
                .filter(p => p.status === 'failed')
                .map(p => ({ slug: p.slug, title: p.title, issues: p.issues }));
              const failedLayouts = layoutsInfo
                .filter(l => l.status === 'failed')
                .map(l => ({ name: l.name, issues: l.issues }));

              const completionAdditions: Partial<RoaryCompletionData> = summary
                ? {
                    pagesVerified: summary.pagesVerified,
                    pagesFailed: summary.pagesFailed,
                    failedPages,
                    layoutsVerified: summary.layoutsVerified,
                    layoutsFailed: summary.layoutsFailed,
                    failedLayouts,
                    stopReason,
                  }
                : {};

              const newCompletion: RoaryCompletionData | null = data.siteId
                ? {
                    siteId: data.siteId as string,
                    siteName: (data.siteName as string) || '',
                    pagesCreated: summary?.pagesPlanned ?? 0,
                    componentsCreated: 0,
                    homePageId: data.homePageId as string | undefined,
                    pageId: (data.pageId as string | undefined) || undefined,
                    pageTitle: (data.pageTitle as string | undefined) || undefined,
                    pageSlug: (data.pageSlug as string | undefined) || undefined,
                    mode: data.pageId ? 'page' : undefined,
                    ...completionAdditions,
                  }
                : prev.completionData
                  ? { ...prev.completionData, ...completionAdditions }
                  : prev.completionData;

              return {
                ...prev,
                buildProgress: updatedBuildProgress,
                completionData: newCompletion,
              };
            });
            break;
          }
        }
      };

      const handleError = (error: Error) => {
        setState(prev => ({
          ...prev,
          error: error.message,
          isStreaming: false,
          isThinking: false,
          currentToolName: null,
          phase:
            siteBuildFinishedRef.current && prev.completionData?.siteId ? 'complete' : prev.phase,
        }));
        updateLastAssistantMessage({ isStreaming: false });
      };

      const handleComplete = () => {
        setState(prev => ({
          ...prev,
          isStreaming: false,
          isThinking: false,
          currentToolName: null,
        }));
        updateLastAssistantMessage({ isStreaming: false });
      };

      abortRef.current = roaryApi.sendMessage(
        currentSessionId,
        content,
        handleEvent,
        handleError,
        handleComplete
      );
    },
    [state.isStreaming, updateLastAssistantMessage]
  );

  // Drop a leftover welcome-back sitting on top of the first greeting
  // (from an older restore). Keeps the open chat from showing two intros.
  useEffect(() => {
    setState(prev => {
      if (prev.phase !== 'greeting' || prev.messages.some(m => m.role === 'user')) {
        return prev;
      }
      const next = prev.messages.filter(m => !m.id.startsWith('welcome-back-'));
      return next.length === prev.messages.length ? prev : { ...prev, messages: next };
    });
  }, [state.phase, state.messages]);

  // ─── Auto-save Session to localStorage ──────────────────────────────────────
  // Persists conversation state so Roary remembers across close/reopen/refresh.

  useEffect(() => {
    if (state.sessionId && !state.isStreaming && !state.isLoading) {
      saveSessionToStorage(state);
    }
  }, [state]);

  // ─── Public API: handleQuickReply ───────────────────────────────────────────

  const handleQuickReply = useCallback(
    (reply: QuickReply) => {
      // Send the quick reply label as a message to the backend
      streamToBackend(reply.label);
    },
    [streamToBackend]
  );

  // ─── Public API: sendMessage ────────────────────────────────────────────────
  // All messages are sent to the backend — Claude handles conversation intelligence.

  const sendMessage = useCallback(
    (content: string) => {
      streamToBackend(content);
    },
    [streamToBackend]
  );

  // ─── Return ─────────────────────────────────────────────────────────────────

  return {
    ...state,
    startSession,
    sendMessage,
    handleQuickReply,
    cancelSession,
    clearError,
    loadSession,
    restoreSession,
  };
};
